import React, { useEffect, useState, useContext } from "react";
import {
  Drawer,
  Empty,
  Flex,
  Input,
  Modal,
  Popover,
  Table,
  Button,
  Space,
} from "antd";
import {
  EditOutlined,
  EditFilled,
  DeleteOutlined,
  PlusOutlined,
  CloseOutlined,
  RedoOutlined,
  ArrowLeftOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { jsonrepair } from "jsonrepair";

import { ComponentsContext } from "../index";
import { SortableList } from "../SortableList";
import { ActionButton } from "../../../components/elements/Button";
import { DropdownMenu } from "../../../components/elements/DropdownMenu";
import {
  defaultParameterNotation,
  NestedObjectTable,
} from "../../../components/elements/JsonObjTable";
import { defaultMenuProps } from "../defaultEventTemplates";

import "./styles.scss";

const { TextArea } = Input;

const generateUniqueId = () => uuidv4();

const templateElemsList = (items) => {
  if (!items || items.length === 0) {
    return [
      defaultParameterNotation({
        key: "",
        type: "string",
        required: true,
        exatclyMatch: false,
        value: "",
      }),
    ];
  }
  return items;
};

const defaultComponentObject = () => ({
  componentId: generateUniqueId(),
  componentName: "",
  description: "",
  steps: [],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

const ComponentsMenu = () => {
  const {
    configParams = {},
    setConfigParams = () => {},
    reusableComponents = [],
    scenariosList = [],
    drawerGlobalState = false,
    setDrawerGlobalState = () => {},
    saveFunction = () => {},
    defaultEventTemplates = [],
    simplifyDLGEventJson = () => {},
    transformDataObject = () => {},
    error = () => {},
    success = () => {},
    peekComponentId = null,
    clearPeekComponentId = () => {},
    peekScenario = () => {},
  } = useContext(ComponentsContext);

  const defaultPlacement = "right";
  const firstDrawerWidth = "75%";
  const secondDrawerWidth = "75%";
  const thirdDrawerWidth = "75%";

  // Components list
  const [curComponentsList, setCurComponentsList] = useState([]);
  useEffect(() => {
    setCurComponentsList(reusableComponents);
  }, [reusableComponents]);

  // Editor drawer
  const [secondDrawerVisible, setSecondDrawerVisible] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [componentName, setComponentName] = useState("");
  const [componentDescription, setComponentDescription] = useState("");
  const [componentSteps, setComponentSteps] = useState([]);
  const [unsavedModalVisible, setUnsavedModalVisible] = useState(false);

  // Delete modal
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [targetComponentId, setTargetComponentId] = useState(null);
  const [targetComponentUsageCount, setTargetComponentUsageCount] = useState(0);

  // DataLayer template drawer
  const [thirdDrawerVisible, setThirdDrawerVisible] = useState(false);
  const [thirdDrawerClassName, setThirdDrawerClassName] = useState("");
  const [templateModalContentCode, setTemplateModalContentCode] = useState("main");
  const [selectedStepId, setSelectedStepId] = useState(null);
  // Action type of the step the template drawer was opened for. Drives the
  // cookie-specific fields/labels in NestedObjectTable for check_cookie_value.
  const [selectedStepType, setSelectedStepType] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplateStr, setSelectedTemplateStr] = useState(null);
  const [jsonBtnText, setJsonBtnText] = useState("add json");
  const [jsonBtnIcon, setJsonBtnIcon] = useState(<PlusOutlined />);
  const [dlEditModalTitle, setDlEditModalTitle] = useState("");
  const [dlEditModalState, setDlEditModalState] = useState(false);
  const [dlEditModalContent, setDlEditModalContent] = useState(false);
  const [editedTextfieldValue, setEditedTextfieldValue] = useState("");

  useEffect(() => {
    const widthClass = templateModalContentCode === "edit" ? "modal-wide" : "";
    setThirdDrawerClassName(widthClass);
  }, [templateModalContentCode, thirdDrawerVisible]);

  useEffect(() => {
    const isInitial =
      selectedTemplate?.length === 1 &&
      selectedTemplate[0].key === "" &&
      selectedTemplate[0].value === "";
    setJsonBtnText(isInitial ? "add json" : "edit json");
    setJsonBtnIcon(isInitial ? <PlusOutlined /> : <EditFilled />);
  }, [selectedTemplate]);

  const menuProps = {
    items: defaultMenuProps,
    onClick: (e) => addDefaultEcommerceObject(e?.key),
  };

  const addDefaultEcommerceObject = (eventName) => {
    const obj = defaultEventTemplates[eventName];
    setSelectedTemplate(obj);
    setSelectedTemplateStr(JSON.stringify(obj));
  };

  const clearJSON = () => {
    setSelectedTemplate(null);
    setSelectedTemplateStr(null);
  };

  const showThirdDrawer = (item, stepId, stepType = "") => {
    const parametersList = templateElemsList(item);
    setSelectedTemplate(parametersList);
    setSelectedTemplateStr(JSON.stringify(parametersList) || "");
    setSelectedStepId(stepId);
    setSelectedStepType(stepType);
    setTemplateModalContentCode("main");
    setThirdDrawerVisible(true);
  };

  const closeThirdDrawer = () => {
    setThirdDrawerVisible(false);
    setTemplateModalContentCode("main");
  };

  const defaultModalContent = () => {
    const placeholderObj = {
      event: "event_name",
      ecommerce: {
        currency: "string",
        value: "number",
        items: [{ item_id: "string", item_name: "string" }],
      },
    };
    const placeholderText = JSON.stringify(placeholderObj, undefined, 2);
    const defaultValueTemplate = simplifyDLGEventJson(selectedTemplate);
    const defaultValueStr = JSON.stringify(defaultValueTemplate, null, 2);
    setEditedTextfieldValue(defaultValueStr);
    setDlEditModalTitle("Template DataLayer object");
    return (
      <>
        <p>Add your dataLayer template in the box below.</p>
        <TextArea
          name="datalayer_object_input"
          key={editedTextfieldValue}
          rows={12}
          placeholder={placeholderText}
          defaultValue={defaultValueStr}
          onInput={(e) => setEditedTextfieldValue(e.target.value)}
          style={{
            fontFamily: "monospace",
            fontSize: "14px",
            backgroundColor: "#f7f7f7",
            border: "1px solid #ccc",
            padding: "10px",
            borderRadius: "4px",
            whiteSpace: "pre-wrap",
          }}
        />
      </>
    );
  };

  const handleEditTemplateEvent = () => {
    setDlEditModalContent(defaultModalContent());
    setDlEditModalState(true);
  };

  const saveEditedDlObjectData = (value) => {
    const transformedValue = transformDataObject(JSON.parse(value));
    setSelectedTemplate(transformedValue);
    setSelectedTemplateStr(JSON.stringify(transformedValue));
    handleSaveTemplate(transformedValue);
    setDlEditModalState(false);
  };

  const handleEditDlModalOk = () => {
    try {
      const value = jsonrepair(
        editedTextfieldValue || JSON.stringify({ "": "" }),
      );
      saveEditedDlObjectData(value);
    } catch (err) {
      console.log(err);
      error("Incorrect value! Please check the JSON object and try again.");
    }
  };

  const handleEditDlModalCancel = () => setDlEditModalState(false);

  const handleSaveTemplate = (template = []) => {
    const updatedSteps = componentSteps.map((elem) =>
      elem.stepId === selectedStepId
        ? { ...elem, dataLayerObjToFind: template }
        : elem,
    );
    const simplified = simplifyDLGEventJson(template);
    setEditedTextfieldValue(JSON.stringify(simplified));
    setSelectedTemplate(template);
    setSelectedTemplateStr(JSON.stringify(template));
    setComponentSteps(updatedSteps);
  };

  // Detect unsaved edits in the editor drawer so we can guard accidental closes.
  const hasUnsavedChanges = (() => {
    if (!secondDrawerVisible || !selectedComponent) return false;
    const isExisting = curComponentsList.some(
      (c) => c.componentId === selectedComponent.componentId,
    );
    if (isExisting) {
      return (
        componentName !== (selectedComponent.componentName || "") ||
        componentDescription !== (selectedComponent.description || "") ||
        JSON.stringify(componentSteps) !==
          JSON.stringify(selectedComponent.steps || [])
      );
    }
    // New component — any input counts as unsaved.
    return (
      componentName.trim() !== "" ||
      componentDescription.trim() !== "" ||
      componentSteps.length > 0
    );
  })();

  const requestCloseEditor = () => {
    if (hasUnsavedChanges) {
      setUnsavedModalVisible(true);
      return;
    }
    setSecondDrawerVisible(false);
  };

  const discardAndCloseEditor = () => {
    setUnsavedModalVisible(false);
    setSecondDrawerVisible(false);
  };

  // Save trigger
  const [isConfigParamsUpdatedBeforeSave, setIsConfigParamsUpdatedBeforeSave] =
    useState(false);

  useEffect(() => {
    if (isConfigParamsUpdatedBeforeSave) {
      saveFunction();
      setIsConfigParamsUpdatedBeforeSave(false);
    }
  }, [isConfigParamsUpdatedBeforeSave]);

  // Count how many scenarios reference a given component
  const getComponentUsageCount = (componentId) => {
    let count = 0;
    const countInSteps = (steps) => {
      if (!Array.isArray(steps)) return;
      steps.forEach((item) => {
        if (item?.blockType === "component_ref" && item?.componentId === componentId) {
          count++;
        }
      });
    };

    scenariosList.forEach((scenario) => {
      countInSteps(scenario?.steps);
      countInSteps(scenario?.cookiesAcceptance?.steps);
      countInSteps(scenario?.loginInfo?.steps);
    });

    return count;
  };

  // Per-scenario breakdown of where a component is referenced. Used by the
  // "view usages" modal so users know exactly which scenarios will be touched
  // before they rename or delete.
  const getComponentUsages = (componentId) => {
    const slotLabels = {
      steps: "Steps",
      cookies: "Cookies acceptance",
      login: "Login flow",
    };
    const countInSteps = (steps) => {
      if (!Array.isArray(steps)) return 0;
      return steps.filter(
        (item) =>
          item?.blockType === "component_ref" &&
          item?.componentId === componentId,
      ).length;
    };

    const usages = [];
    scenariosList.forEach((scenario) => {
      const slots = [];
      const stepsCount = countInSteps(scenario?.steps);
      if (stepsCount > 0) slots.push({ slot: slotLabels.steps, count: stepsCount });
      const cookiesCount = countInSteps(scenario?.cookiesAcceptance?.steps);
      if (cookiesCount > 0) slots.push({ slot: slotLabels.cookies, count: cookiesCount });
      const loginCount = countInSteps(scenario?.loginInfo?.steps);
      if (loginCount > 0) slots.push({ slot: slotLabels.login, count: loginCount });

      if (slots.length > 0) {
        usages.push({
          scenarioID: scenario.scenarioID,
          scenarioTitle: scenario.scenarioTitle || "Untitled Scenario",
          slots,
        });
      }
    });
    return usages;
  };

  // Usage popover — tracks which component's "Used in" popover is open. Popover
  // is preferred over a Modal here because the global modal styles in
  // AccountSettings/styles.scss kill `pointer-events` on `.ant-modal-wrap` and
  // hide `.ant-modal-close`, making outside-click and X-button dismissal
  // unreliable.
  const [usagePopoverComponentId, setUsagePopoverComponentId] = useState(null);

  const renderUsagePopoverContent = (componentId) => {
    const usages = getComponentUsages(componentId);
    if (usages.length === 0) {
      return <p style={{ margin: 0 }}>This component is not used in any scenario.</p>;
    }
    return (
      <ul style={{ paddingLeft: 20, margin: 0, maxWidth: 320 }}>
        {usages.map((u) => (
          <li key={u.scenarioID} style={{ marginBottom: 6 }}>
            <Button
              type="link"
              style={{ padding: 0, height: "auto", fontWeight: 600 }}
              onClick={(e) => {
                // React events bubble through portals — without
                // stopPropagation, this click would also fire the table
                // row's onClick (which opens the component editor).
                e.stopPropagation();
                setUsagePopoverComponentId(null);
                peekScenario(u.scenarioID);
              }}
            >
              {u.scenarioTitle}
            </Button>
            {" — "}
            {u.slots
              .map((s) => (s.count > 1 ? `${s.slot} (${s.count}×)` : s.slot))
              .join(", ")}
          </li>
        ))}
      </ul>
    );
  };

  // Table columns
  const headerStyle = {
    background: "#fff",
    backgroundImage: "none",
    boxShadow: "none",
    filter: "none",
  };

  const formatUpdatedAt = (iso) => {
    if (!iso) return "—";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const componentColumns = [
    {
      title: "Component name",
      dataIndex: "componentName",
      key: "componentName",
      align: "left",
      render: (text) => text || "Untitled Component",
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "Steps",
      key: "stepCount",
      align: "center",
      render: (_, record) => record?.steps?.length || 0,
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "Last updated",
      key: "updatedAt",
      align: "center",
      render: (_, record) => formatUpdatedAt(record?.updatedAt),
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "Used in",
      key: "usageCount",
      align: "center",
      render: (_, record) => {
        const count = getComponentUsageCount(record?.componentId);
        const label = `${count} scenario${count !== 1 ? "s" : ""}`;
        if (count === 0) return label;
        const componentTitle = record?.componentName || "Untitled Component";
        return (
          <Popover
            trigger="click"
            placement="left"
            open={usagePopoverComponentId === record?.componentId}
            onOpenChange={(open) =>
              setUsagePopoverComponentId(open ? record?.componentId : null)
            }
            title={`Scenarios using "${componentTitle}"`}
            content={renderUsagePopoverContent(record?.componentId)}
          >
            <Button
              size="small"
              type="link"
              style={{ padding: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {label}
            </Button>
          </Popover>
        );
      },
      onHeaderCell: () => ({ style: headerStyle }),
    },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, record) => (
        <Space size={4}>
          <Button
            size="small"
            type="text"
            icon={<DeleteOutlined />}
            aria-label="Delete component"
            style={{ color: "#000" }}
            onClick={(e) => {
              e.stopPropagation();
              confirmDeleteComponent(record?.componentId);
            }}
          />
          <Button
            size="small"
            type="text"
            icon={<EditOutlined />}
            aria-label="Edit component"
            onClick={(e) => {
              e.stopPropagation();
              handleEditComponentClick(record?.componentId);
            }}
          />
        </Space>
      ),
      onHeaderCell: () => ({ style: headerStyle }),
    },
  ];

  // CRUD handlers
  const handleAddComponentClick = () => {
    const newComponent = defaultComponentObject();
    setSelectedComponent(newComponent);
    setComponentName("");
    setComponentDescription("");
    setComponentSteps([]);
    setSecondDrawerVisible(true);
  };

  const handleEditComponentClick = (componentId) => {
    const component = curComponentsList?.find(
      (c) => c?.componentId === componentId,
    );
    if (!component) return;

    // Deep-clone the steps so SortableItem's in-place mutations on the editor
    // copy don't bleed into selectedComponent.steps, otherwise the dirty-check
    // below sees both arrays as identical and the discard prompt never fires.
    const stepsClone = JSON.parse(JSON.stringify(component.steps || []));
    setSelectedComponent(component);
    setComponentName(component.componentName || "");
    setComponentDescription(component.description || "");
    setComponentSteps(stepsClone);
    setSecondDrawerVisible(true);
  };

  // Peek mode: when a sibling menu (e.g. scenarios) wants to show a component
  // editor in-place over its own drawer, it sets `peekComponentId`. We open
  // the inner editor directly — independent of the outer components drawer,
  // since antd portals each Drawer to <body>. Closing the inner editor
  // returns the user to wherever they were.
  useEffect(() => {
    if (!peekComponentId) return;
    if (!Array.isArray(curComponentsList) || curComponentsList.length === 0)
      return;
    const exists = curComponentsList.some(
      (c) => c?.componentId === peekComponentId,
    );
    if (!exists) {
      clearPeekComponentId();
      return;
    }
    handleEditComponentClick(peekComponentId);
    clearPeekComponentId();
  }, [peekComponentId, curComponentsList]);

  const confirmDeleteComponent = (id) => {
    setTargetComponentId(id);
    setTargetComponentUsageCount(getComponentUsageCount(id));
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirmed = () => {
    if (!targetComponentId) return;
    setDeleteModalVisible(false);
    componentRemove(targetComponentId);
    setTargetComponentId(null);
  };

  // Auto-detach all references before deleting a component
  const componentRemove = (id) => {
    const component = curComponentsList.find((c) => c.componentId === id);
    const componentStepsToClone = component?.steps || [];

    // Track how many scenarios were touched so we can report it to the user.
    const detachedScenarioIds = new Set();

    const detachInSteps = (steps, scenarioId) => {
      if (!Array.isArray(steps)) return steps;
      const result = [];
      steps.forEach((item) => {
        if (item?.blockType === "component_ref" && item?.componentId === id) {
          detachedScenarioIds.add(scenarioId);
          componentStepsToClone.forEach((step) => {
            result.push({ ...step, stepId: generateUniqueId() });
          });
        } else {
          result.push(item);
        }
      });
      return result;
    };

    const updatedScenarios = scenariosList.map((scenario) => ({
      ...scenario,
      steps: detachInSteps(scenario.steps, scenario.scenarioID),
      cookiesAcceptance: {
        ...scenario.cookiesAcceptance,
        steps: detachInSteps(
          scenario.cookiesAcceptance?.steps,
          scenario.scenarioID,
        ),
      },
      loginInfo: {
        ...scenario.loginInfo,
        steps: detachInSteps(scenario.loginInfo?.steps, scenario.scenarioID),
      },
    }));

    const newComponentsList = curComponentsList.filter(
      (c) => c.componentId !== id,
    );
    setCurComponentsList(newComponentsList);

    const updated = {
      ...configParams,
      reusable_components: newComponentsList,
      scenarios: updatedScenarios,
    };
    setConfigParams(updated);
    setIsConfigParamsUpdatedBeforeSave(true);

    const detachedCount = detachedScenarioIds.size;
    if (detachedCount > 0) {
      success(
        `Component deleted. Detached from ${detachedCount} scenario${detachedCount !== 1 ? "s" : ""}.`,
      );
    } else {
      success("Component deleted.");
    }
  };

  // Duplicate-name soft warning state
  const [duplicateNameModalVisible, setDuplicateNameModalVisible] =
    useState(false);

  const hasDuplicateName = () => {
    if (!selectedComponent) return false;
    const trimmed = componentName.trim().toLowerCase();
    if (!trimmed) return false;
    return curComponentsList.some(
      (c) =>
        c.componentId !== selectedComponent.componentId &&
        (c.componentName || "").trim().toLowerCase() === trimmed,
    );
  };

  // Save component (create or update). Shows a soft warning when the name
  // collides with another component — duplicates are allowed but ambiguous in
  // the insert dropdown, so we surface it rather than silently saving.
  const handleSaveComponent = () => {
    if (!selectedComponent) return;
    if (hasDuplicateName()) {
      setDuplicateNameModalVisible(true);
      return;
    }
    performSaveComponent();
  };

  const performSaveComponent = () => {
    if (!selectedComponent) return;
    setDuplicateNameModalVisible(false);

    const now = new Date().toISOString();
    const updatedComponent = {
      ...selectedComponent,
      componentName,
      description: componentDescription,
      steps: componentSteps,
      updatedAt: now,
    };

    // Check if this is a new component or existing
    const existingIndex = curComponentsList.findIndex(
      (c) => c.componentId === updatedComponent.componentId,
    );

    let newList;
    if (existingIndex >= 0) {
      newList = [...curComponentsList];
      newList[existingIndex] = updatedComponent;
    } else {
      updatedComponent.createdAt = now;
      newList = [...curComponentsList, updatedComponent];
    }

    setCurComponentsList(newList);
    setSecondDrawerVisible(false);

    const updated = { ...configParams, reusable_components: newList };
    setConfigParams(updated);
    setIsConfigParamsUpdatedBeforeSave(true);
  };

  return (
    <div>
      {/* Components list drawer */}
      <Drawer
        className="components-drawer"
        placement={defaultPlacement}
        zIndex={50}
        onClose={() => {
          if (thirdDrawerVisible) return;
          // While the delete-confirm modal is open, route the drawer's
          // close trigger (Esc / mask click) to dismiss the modal first
          // instead of closing the drawer.
          if (deleteModalVisible) {
            setDeleteModalVisible(false);
            setTargetComponentId(null);
            return;
          }
          // If the editor is open, intercept and route through its own
          // close path so unsaved changes get the confirm prompt instead of
          // being silently discarded with the parent drawer.
          if (secondDrawerVisible) {
            requestCloseEditor();
            return;
          }
          setDrawerGlobalState(false);
        }}
        open={drawerGlobalState}
        forceRender
        closeIcon={null}
        closable={true}
        maskClosable={!thirdDrawerVisible}
        width={firstDrawerWidth}
        styles={{ header: { display: "none" } }}
      >
        <div className="modal-elems-rows scenarios-modal-padding">
          <div>
            <h2 className="header-letter-spacing">Reusable components</h2>
          </div>
          <div className="modal-text">
            <p>
              Create reusable step components that can be inserted into multiple
              scenarios. When you update a component, all scenarios using it will
              automatically reflect the changes.
            </p>
          </div>

          <div className="no-sep-header">
            <Table
              size="small"
              columns={componentColumns}
              dataSource={curComponentsList}
              rowKey="componentId"
              rowClassName="components-list-row"
              onRow={(record) => ({
                onClick: () => handleEditComponentClick(record?.componentId),
              })}
              pagination={false}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div style={{ maxWidth: 360, margin: "0 auto" }}>
                        <p style={{ marginBottom: 4, fontWeight: 600 }}>
                          No reusable components yet
                        </p>
                        <p style={{ color: "#666", fontSize: "0.9em" }}>
                          Create a component to share a sequence of steps
                          across multiple scenarios. Updates to the component
                          flow through to every scenario that uses it.
                        </p>
                      </div>
                    }
                  >
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleAddComponentClick}
                    >
                      Create your first component
                    </Button>
                  </Empty>
                ),
              }}
            />
          </div>
          <div
            style={{
              marginTop: "32px",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "flex-end",
              gap: "18px",
            }}
          >
            <Button type="default" onClick={handleAddComponentClick}>
              + Add component
            </Button>
          </div>

          <Modal
            centered
            open={deleteModalVisible}
            title="Delete this component?"
            onOk={handleDeleteConfirmed}
            onCancel={() => {
              setDeleteModalVisible(false);
              setTargetComponentId(null);
            }}
            okType="saveChanges"
            zIndex={2000}
          >
            {targetComponentUsageCount > 0 ? (
              <p>
                This component is used in{" "}
                <strong>{targetComponentUsageCount}</strong>{" "}
                scenario{targetComponentUsageCount !== 1 ? "s" : ""}. Deleting
                it will automatically detach all instances (steps will become
                independent). Are you sure?
              </p>
            ) : (
              <p>Are you sure you want to delete this component?</p>
            )}
          </Modal>
        </div>

        {/* Component editor drawer */}
        {/* Explicit zIndex so peek-mode (opened from the scenarios drawer) stacks
            above the scenario edit drawer; antd's auto-bump doesn't apply because
            this drawer's parent components-drawer is closed in peek mode. */}
        <Drawer
          className="component-editor-drawer scenarios-drawer"
          placement={defaultPlacement}
          push={{ distance: 240 }}
          onClose={() => {
            // Esc on the third (template) drawer can fall through to this
            // handler — guard against the cascade so the editor stays open
            // while the template drawer is the active one.
            if (thirdDrawerVisible) return;
            requestCloseEditor();
          }}
          open={secondDrawerVisible}
          closeIcon={null}
          closable={true}
          maskClosable={!thirdDrawerVisible}
          width={secondDrawerWidth}
          zIndex={1500}
          styles={{ header: { display: "none" } }}
          footer={
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                gap: 12,
                padding: "8px 0",
              }}
            >
              <ActionButton
                size="large"
                type="save"
                width={140}
                disabled={!componentName.trim() || !hasUnsavedChanges}
                onClickFunction={handleSaveComponent}
              >
                Save
              </ActionButton>
              <Button size="large" onClick={requestCloseEditor}>
                Cancel
              </Button>
            </div>
          }
        >
          <div className="modal-elems-rows scenarios-modal-padding">
            <div>
              <h2 className="header-letter-spacing">
                {selectedComponent &&
                curComponentsList.some(
                  (c) => c.componentId === selectedComponent.componentId,
                )
                  ? "Edit Component"
                  : "New Component"}
              </h2>
            </div>

            <div style={{ marginTop: "1rem" }}>
              <label
                style={{ fontWeight: 600, display: "block", marginBottom: 4 }}
              >
                Component name
              </label>
              <Input
                value={componentName}
                onChange={(e) => setComponentName(e.target.value)}
                placeholder="e.g. Cookie Consent Flow"
              />
            </div>

            {/*
              Description field intentionally hidden — feature decision
              (2026-05-13) to keep the component editor minimal. Re-enable
              by uncommenting if a description field comes back on the spec.
            */}
            {/*
            <div style={{ marginTop: "1rem" }}>
              <label
                style={{ fontWeight: 600, display: "block", marginBottom: 4 }}
              >
                Description (optional)
              </label>
              <Input.TextArea
                value={componentDescription}
                onChange={(e) => setComponentDescription(e.target.value)}
                placeholder="Describe what this component does..."
                rows={2}
              />
            </div>
            */}

            <div className="component-steps-section">
              <label
                style={{ fontWeight: 600, display: "block", marginBottom: 8 }}
              >
                Steps
              </label>
              <p style={{ color: "#666", marginBottom: 12, fontSize: "0.9em" }}>
                Define the steps this component should execute. These steps will
                be shared across all scenarios that use this component.
              </p>
              {/*
                Intentionally NOT passing reusableComponents / resolvers /
                onInsertComponent here. Without them the SortableList omits
                the "Insert component" UI, which prevents nested component
                refs. There is no cycle detection on component_ref blocks,
                so allowing nesting could create infinite resolution loops.
                If a future change wires those props in, add cycle detection
                first.
              */}
              <SortableList
                items={componentSteps}
                handleDataLayerObjEdit={showThirdDrawer}
                handleStepsUpdate={setComponentSteps}
              />
            </div>

            {/* Save / Cancel buttons moved into Drawer's built-in `footer`
                prop (sticky to the drawer body, not viewport). */}

            {/* Unsaved changes confirmation modal — mirrors the scenarios
                pattern (Save your changes / discard / save changes). */}
            <Modal
              open={unsavedModalVisible}
              centered
              title={
                <div className="closeIconDiv">
                  <span>Save your changes</span>
                  <CloseOutlined
                    onClick={() => setUnsavedModalVisible(false)}
                    className="closeIcon"
                  />
                </div>
              }
              onCancel={() => setUnsavedModalVisible(false)}
              closable={false}
              zIndex={2000}
              footer={[
                <Button
                  key="discard"
                  type="default"
                  onClick={discardAndCloseEditor}
                >
                  discard
                </Button>,
                <Button
                  key="save"
                  type="saveChanges"
                  disabled={!componentName.trim()}
                  onClick={() => {
                    setUnsavedModalVisible(false);
                    handleSaveComponent();
                  }}
                >
                  save changes
                </Button>,
              ]}
            >
              <p>Do you want to save changes before leaving this step?</p>
            </Modal>

            <Modal
              centered
              open={duplicateNameModalVisible}
              title="Component name already exists"
              onOk={performSaveComponent}
              onCancel={() => setDuplicateNameModalVisible(false)}
              okText="Save anyway"
              okType="saveChanges"
              cancelText="Rename"
              zIndex={2000}
            >
              <p>
                Another component is already named{" "}
                <strong>"{componentName.trim()}"</strong>. Duplicate names will
                appear together in the "Insert component" dropdown and may be
                confusing.
              </p>
              <p>Save anyway, or rename this one?</p>
            </Modal>
          </div>

          {/* DataLayer object template drawer — must be nested inside the
              component editor drawer so antd's push translates the editor
              left when this opens, and so Esc/close events don't cascade. */}
          <Drawer
            key={templateModalContentCode}
            classNames={thirdDrawerClassName ? { wrapper: thirdDrawerClassName } : undefined}
            title="DataLayer Template"
            placement={defaultPlacement}
            onClose={closeThirdDrawer}
            open={thirdDrawerVisible}
            closable={true}
            width={thirdDrawerWidth}
            zIndex={2000}
            styles={{ header: { display: "none" } }}
          >
            <Modal
              centered
              title={dlEditModalTitle}
              open={dlEditModalState}
              onOk={handleEditDlModalOk}
              onCancel={handleEditDlModalCancel}
              footer={(_, { OkBtn, CancelBtn }) => (
                <Flex gap={5}>
                  <CancelBtn />
                  <OkBtn />
                </Flex>
              )}
            >
              {dlEditModalContent}
            </Modal>

            <div
              key="template_main"
              className={`modal-elems-rows scenarios-modal-padding ${
                templateModalContentCode === "main" ? "visible" : "hidden"
              }`}
            >
              <div>
                <h2 className="header-submodal header-letter-spacing">
                  DataLayer event template
                </h2>
              </div>
              <div>
                <h3 className="modal-category fw-bold">Add event details</h3>
                <p className="mb-0">
                  To enable monitoring for this event, details on the dataLayer
                  event should be shared in the table below.
                  <br />
                  For a quick start you can copy / paste your JSON object via the
                  button below.
                </p>
              </div>
              <Flex gap={10}>
                <ActionButton
                  text={jsonBtnText}
                  type=""
                  icon={jsonBtnIcon}
                  onClickFunction={() => {
                    setTemplateModalContentCode("edit");
                    handleEditTemplateEvent();
                  }}
                  width={120}
                />
                <DropdownMenu
                  text="add ecommerce event"
                  type=""
                  disabled={selectedTemplate?.length > 1}
                  menuProps={menuProps}
                  loading={false}
                  style={{ padding: "12px 20px" }}
                  icon={<DownOutlined />}
                />
                <ActionButton
                  text="clean json"
                  type=""
                  icon={<RedoOutlined />}
                  onClickFunction={clearJSON}
                />
              </Flex>
              <Space>
                <NestedObjectTable
                  key={selectedStepId}
                  data={selectedTemplateStr}
                  stepType={selectedStepType}
                  updateTemplateFunction={setSelectedTemplate}
                  setEditedEventTemplateNewVersion={setSelectedTemplate}
                />
              </Space>
            </div>

            <div
              key="template_edit"
              className={`modal-elems-rows scenarios-modal-padding ${
                templateModalContentCode === "edit" ? "visible" : "hidden"
              }`}
            >
              <div>
                <h2 className="header-submodal header-letter-spacing">
                  Edit DataLayer event template
                </h2>
              </div>
              <div>
                <h3 className="modal-category fw-bold">Add event details</h3>
                <p className="mb-0">
                  To enable monitoring for this event, details on the dataLayer
                  event should be shared in the table below.
                  <br />
                  For a quick start you can copy / paste your JSON object via the
                  button below.
                </p>
              </div>
              <Flex gap={10}>
                <ActionButton
                  text="back"
                  type=""
                  icon={<ArrowLeftOutlined />}
                  onClickFunction={() => setTemplateModalContentCode("main")}
                  width={120}
                />
                <DropdownMenu
                  text="add ecommerce event"
                  type=""
                  disabled={selectedTemplate?.length > 1}
                  menuProps={menuProps}
                  loading={false}
                  style={{ padding: "12px 20px" }}
                  icon={<DownOutlined />}
                />
                <ActionButton
                  text="clean json"
                  type=""
                  icon={<RedoOutlined />}
                  onClickFunction={clearJSON}
                />
              </Flex>
              <Space>
                <NestedObjectTable
                  key={selectedStepId}
                  data={selectedTemplateStr}
                  stepType={selectedStepType}
                  updateTemplateFunction={setSelectedTemplate}
                  setEditedEventTemplateNewVersion={setSelectedTemplate}
                />
              </Space>
            </div>

            <div className="drawer-buttons-area">
              <div className="drawer-buttons-area-shad"></div>
              <div className="drawer-buttons-area-button">
                <ActionButton
                  size="large"
                  type="primary"
                  width={140}
                  onClickFunction={() => {
                    handleSaveTemplate(selectedTemplate);
                    closeThirdDrawer();
                  }}
                >
                  Save
                </ActionButton>
              </div>
            </div>
          </Drawer>
        </Drawer>
      </Drawer>
    </div>
  );
};

export default ComponentsMenu;
